import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "../../../utils/request";

// Create async thunk for fetching dashboard overview
export const fetchDashboardOverview = createAsyncThunk(
  "dashboard/fetchOverview",
  async ({ period, date, completedOnly }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (period) params.append('period', period);
      if (date) params.append('date', date);
      if (completedOnly !== undefined) params.append('completed_only', completedOnly);

      const data = await request(
        `${process.env.REACT_APP_API_URL}/dashboard/overview/?${params.toString()}`,
        { method: "GET" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch dashboard overview"
      );
    }
  }
);

// Create async thunk for fetching dashboard trend
export const fetchDashboardTrend = createAsyncThunk(
  "dashboard/fetchTrend",
  async ({ startDate, endDate, period, completedOnly }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (period) params.append('period', period);
      if (completedOnly !== undefined) params.append('completed_only', completedOnly);

      const data = await request(
        `${process.env.REACT_APP_API_URL}/dashboard/trend/?${params.toString()}`,
        { method: "GET" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch dashboard trend"
      );
    }
  }
);

// Create async thunk for fetching user backgrounds
export const fetchUserBackgrounds = createAsyncThunk(
  "auth/fetchUserBackgrounds",
  async (_, { rejectWithValue }) => {
    try {
      // Using the request utility which automatically adds the token
      const data = await request(
        `${process.env.REACT_APP_API_URL}/auth/user/backgrounds/`,
        { method: "GET" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch user backgrounds"
      );
    }
  }
);

// Helper function to get current date values
const getInitialDateValues = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-indexed
  const monthName = now.toLocaleDateString('en-US', { month: 'long' });

  // Calculate trend date range (6 months ago to now)
  const sixMonthsAgo = new Date(now);
  sixMonthsAgo.setMonth(now.getMonth() - 6);

  return {
    selectedDate: {
      year: currentYear,
      month: currentMonth,
      display: `${monthName} ${currentYear}`,
      shortDisplay: `${String(currentMonth + 1).padStart(2, '0')}/${currentYear}`
    },
    trendStartDate: sixMonthsAgo.toISOString().split('T')[0],
    trendEndDate: now.toISOString().split('T')[0]
  };
};

const initialDateValues = getInitialDateValues();

const initialState = {
  isSurveyComplete: false,
  getSurveyLoading: false,
  error: null,
  // Dashboard overview data
  activeTab: 'overview',
  selectedPeriod: 'monthly',
  selectedDate: initialDateValues.selectedDate,
  completedOnly: true,
  // API data
  overviewData: null,
  overviewLoading: false,
  overviewError: null,
  // Trend data
  trendData: null,
  trendLoading: false,
  trendError: null,
  // Trend-specific settings
  trendPeriod: 'monthly',
  trendStartDate: initialDateValues.trendStartDate,
  trendEndDate: initialDateValues.trendEndDate,
  trendViewOptions: {
    showIncome: true,
    showJobs: true
  },
  statistics: {
    totalIncome: {
      value: '$11,000',
      percentage: '5.6%',
      isPositive: true,
    },
    totalJobs: {
      value: '20',
      percentage: '3.0%',
      isPositive: false,
    },
    avgIncome: {
      value: '$550',
      subtitle: '/per job',
      percentage: '7.1%',
      isPositive: true,
    },
  },
  pieChartData: [
    {
      id: 'top-region',
      title: 'Top Region',
      percentage: 33.6,
      primaryLabel: 'Seattle, WA',
      primaryValue: '$2,700',
    },
    {
      id: 'top-quote-range',
      title: 'Top Quote Range',
      percentage: 40.1,
      primaryLabel: '$500-$599',
      primaryValue: '16 jobs',
    },
    {
      id: 'top-agent',
      title: 'Top Agent',
      percentage: 63.7,
      primaryLabel: 'Jim Moriarty',
      primaryValue: '$7,000',
    },
    {
      id: 'top-property-type',
      title: 'Top Property Type',
      percentage: 63.7,
      primaryLabel: 'Property Type not specified',
      primaryValue: '16 jobs',
    },
  ],
};

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setSurveyComplete: (state, action) => {
      state.isSurveyComplete = action.payload;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setSelectedPeriod: (state, action) => {
      state.selectedPeriod = action.payload;
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setSelectedPeriodWithDate: (state, action) => {
      const period = action.payload;
      state.selectedPeriod = period;

      // Calculate current date value for the selected period
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth(); // 0-indexed
      const currentQuarter = Math.floor(currentMonth / 3) + 1;

      let selectedDate;
      switch (period) {
        case 'monthly':
          selectedDate = {
            year: currentYear,
            month: currentMonth,
            display: `${now.toLocaleDateString('en-US', { month: 'long' })} ${currentYear}`,
            shortDisplay: `${String(currentMonth + 1).padStart(2, '0')}/${currentYear}`
          };
          break;
        case 'quarterly':
          selectedDate = {
            year: currentYear,
            quarter: currentQuarter,
            display: `Q${currentQuarter} ${currentYear}`,
            shortDisplay: `Q${currentQuarter} ${currentYear}`
          };
          break;
        case 'weekly': {
          const currentDate = now.toISOString().split('T')[0];
          const monthShort = now.toLocaleDateString('en-US', { month: 'short' });
          const day = now.getDate();
          selectedDate = {
            date: currentDate,
            display: `${monthShort} ${day}`,
            shortDisplay: `${monthShort} ${day}`
          };
          break;
        }
        case 'yearly':
          selectedDate = {
            year: currentYear,
            display: `${currentYear}`,
            shortDisplay: `${currentYear}`
          };
          break;
        default:
          selectedDate = null;
      }

      state.selectedDate = selectedDate;
    },
    setCompletedOnly: (state, action) => {
      state.completedOnly = action.payload;
    },
    setTrendPeriod: (state, action) => {
      state.trendPeriod = action.payload;
    },
    setTrendDateRange: (state, action) => {
      state.trendStartDate = action.payload.startDate;
      state.trendEndDate = action.payload.endDate;
    },
    setTrendPeriodWithDateRange: (state, action) => {
      const period = action.payload;
      state.trendPeriod = period;

      // Calculate default date range based on period
      const now = new Date();
      const endDate = now.toISOString().split('T')[0];
      let startDate;

      switch (period) {
        case 'monthly':
          // 6 months ago
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(now.getMonth() - 6);
          startDate = sixMonthsAgo.toISOString().split('T')[0];
          break;

        case 'weekly': {
          // 12 weeks ago (about 3 months)
          const twelveWeeksAgo = new Date(now);
          twelveWeeksAgo.setDate(now.getDate() - 12 * 7);
          startDate = twelveWeeksAgo.toISOString().split('T')[0];
          break;
        }

        case 'quarterly':
          // 1 year ago
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(now.getFullYear() - 1);
          startDate = oneYearAgo.toISOString().split('T')[0];
          break;

        case 'yearly':
          // 3 years ago
          const threeYearsAgo = new Date(now);
          threeYearsAgo.setFullYear(now.getFullYear() - 3);
          startDate = threeYearsAgo.toISOString().split('T')[0];
          break;

        default:
          startDate = endDate;
      }

      state.trendStartDate = startDate;
      state.trendEndDate = endDate;
    },
    setTrendViewOptions: (state, action) => {
      state.trendViewOptions = action.payload;
    },
    resetDashboard: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserBackgrounds.pending, (state) => {
        state.getSurveyLoading = true;
      })
      .addCase(fetchUserBackgrounds.fulfilled, (state, action) => {
        state.getSurveyLoading = false;
        state.isSurveyComplete = action.payload.length > 0;
      })
      .addCase(fetchUserBackgrounds.rejected, (state, action) => {
        state.getSurveyLoading = false;
        state.error = action.error.message;
      })
      // Dashboard overview cases
      .addCase(fetchDashboardOverview.pending, (state) => {
        state.overviewLoading = true;
        state.overviewError = null;
      })
      .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
        state.overviewLoading = false;
        state.overviewData = action.payload;
        
        // Update statistics from API data
        const { current_period, previous_period, comparison } = action.payload;
        state.statistics = {
          totalIncome: {
            value: `$${current_period.total_income.toLocaleString()}`,
            percentage: `${Math.abs(comparison.income_change_percent)}%`,
            isPositive: comparison.income_change_percent >= 0,
          },
          totalJobs: {
            value: current_period.total_jobs.toString(),
            percentage: `${Math.abs(comparison.jobs_change_percent)}%`,
            isPositive: comparison.jobs_change_percent >= 0,
          },
          avgIncome: {
            value: `$${current_period.avg_income.toFixed(0)}`,
            subtitle: '/per job',
            percentage: `${Math.abs(comparison.avg_income_change_percent)}%`,
            isPositive: comparison.avg_income_change_percent >= 0,
          },
          prevTotalIncome: previous_period ? `$${previous_period.total_income.toLocaleString()}` : '$0',
          prevTotalJobs: previous_period ? previous_period.total_jobs.toString() : '0',
          prevAvgIncome: previous_period ? `$${previous_period.avg_income.toFixed(0)}` : '$0',
          currentPeriodStart: current_period?.period_start || null,
          previousPeriodStart: previous_period?.period_start || null,
        };
        
        // Update analytics from API data
        const { top_metrics } = action.payload;
        state.analytics = {
          topRegion: {
            name: top_metrics?.top_region?.regions?.[0]?.location || 'N/A',
            value: `$${top_metrics?.top_region?.regions?.[0]?.total_income?.toLocaleString() || '0'}`,
            percentage: `${top_metrics?.top_region?.regions?.[0]?.percentage || 0}%`
          },
          topQuoteRange: {
            name: top_metrics?.top_quote_range?.quote_ranges?.[0]?.range || 'N/A',
            value: `${top_metrics?.top_quote_range?.quote_ranges?.[0]?.job_count || 0} jobs`,
            percentage: `${top_metrics?.top_quote_range?.quote_ranges?.[0]?.percentage || 0}%`
          },
          topAgent: {
            name: top_metrics?.top_agent_income?.agents?.[0]?.agent?.name || 'N/A',
            value: `$${top_metrics?.top_agent_income?.agents?.[0]?.total_income?.toLocaleString() || '0'}`,
            percentage: `${top_metrics?.top_agent_income?.agents?.[0]?.income_percentage || 0}%`
          },
          topPropertyType: {
            name: top_metrics?.top_property_type?.property_types?.[0]?.type || 'N/A',
            value: `${top_metrics?.top_property_type?.property_types?.[0]?.job_count || 0} jobs`,
            percentage: `${top_metrics?.top_property_type?.property_types?.[0]?.percentage || 0}%`
          }
        };
      })
      .addCase(fetchDashboardOverview.rejected, (state, action) => {
        state.overviewLoading = false;
        state.overviewError = action.payload || action.error.message;
      })
      // Dashboard trend cases
      .addCase(fetchDashboardTrend.pending, (state) => {
        state.trendLoading = true;
        state.trendError = null;
      })
      .addCase(fetchDashboardTrend.fulfilled, (state, action) => {
        state.trendLoading = false;
        state.trendData = action.payload;
      })
      .addCase(fetchDashboardTrend.rejected, (state, action) => {
        state.trendLoading = false;
        state.trendError = action.payload || action.error.message;
      });
  },
});

export const {
  setSurveyComplete,
  setActiveTab,
  setSelectedPeriod,
  setSelectedDate,
  setSelectedPeriodWithDate,
  setCompletedOnly,
  setTrendPeriod,
  setTrendDateRange,
  setTrendPeriodWithDateRange,
  setTrendViewOptions,
  resetDashboard
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
